@if (currentPage() === 0) {
  <!-- Page 1: Issue and Desired Outcome -->
  <form [formGroup]="form" class="p-6">
    <!-- Issue Description -->
    <div class="mb-6">
      <label class="block pb-1 text-sm font-medium">
        What's the issue?
        <span class="text-[#dc3545]">*</span>
      </label>
      <textarea
        nz-input
        formControlName="issueDescription"
        placeholder="Explain the issue clearly and briefly. Tell us what you expected and how you'd like it resolved."
        [rows]="5"
        class="w-full"
        [class.border-red-500]="form.get('issueDescription')?.invalid && form.get('issueDescription')?.touched"
      ></textarea>
      @if (form.get('issueDescription')?.invalid && form.get('issueDescription')?.touched) {
        <div class="mt-1 text-[#dc3545] text-xs">Issue description is required</div>
      }
    </div>

    <!-- Desired Outcome -->
    <div class="mb-6">
      <label class="block pb-1 text-sm font-medium">
        Desired outcome
        <span class="text-[#dc3545]">*</span>
      </label>
      <nz-select
        formControlName="desiredOutcome"
        nzPlaceHolder="Select desired outcome..."
        nzShowSearch
        [nzOptionOverflowSize]="999"
        class="w-full desired-outcome-select"
        [nzStatus]="form.get('desiredOutcome')?.invalid && form.get('desiredOutcome')?.touched ? 'error' : ''"
      >
        @for (item of dropdownItems; track item.code) {
          <nz-option [nzValue]="item.code" [nzLabel]="item.label"></nz-option>
        }
      </nz-select>
      @if (form.get('desiredOutcome')?.invalid && form.get('desiredOutcome')?.touched) {
        <div class="mt-1 text-[#dc3545] text-xs">Desired outcome is required</div>
      }
    </div>

    <!-- Conditional Reason Field -->
    @if (selectedOption()?.requiresReason) {
      <div class="mb-6">
        <label class="block pb-1 text-sm font-medium">
          {{ selectedOption()?.reasonLabel }}
          <span class="text-[#dc3545]">*</span>
        </label>
        <textarea
          nz-input
          formControlName="reason"
          [placeholder]="selectedOption()?.reasonPlaceholder || 'State your reason'"
          [rows]="5"
          class="w-full"
          [class.border-red-500]="form.get('reason')?.invalid && form.get('reason')?.touched"
        ></textarea>
        @if (form.get('reason')?.invalid && form.get('reason')?.touched) {
          <div class="mt-1 text-[#dc3545] text-xs">Reason is required</div>
        }
      </div>
    }

    <!-- File Upload - Show when desired outcome is selected -->
    @if (desiredOutcomeValue()) {
      <div class="mb-6">
        <label class="block pb-1 text-sm font-medium mb-2">Upload evidence(s) here (Optional)</label>
        <nz-upload
          nzType="drag"
          [nzMultiple]="true"
          [nzAccept]="'image/jpeg,image/png,image/jpg,image/gif,application/pdf'"
          [nzBeforeUpload]="beforeUpload"
          [nzShowUploadList]="false"
          class="w-full"
        >
          <p class="ant-upload-drag-icon">
            <i nz-icon nzType="upload" nzTheme="outline" class="text-4xl text-gray-400"></i>
          </p>
          <p class="ant-upload-text">Drag or upload files</p>
          <p class="ant-upload-hint">Upload JPEG, PNG, PDF, GIF (10MB)</p>
        </nz-upload>

        <!-- File List -->
        @if (files().length > 0) {
          <div class="mt-4 space-y-2">
            @for (file of files(); track $index; let index = $index) {
              <div class="flex items-center justify-between p-2 bg-gray-100 rounded">
                <span class="text-sm truncate flex-1">{{ file.name }}</span>
                <button
                  type="button"
                  nz-button
                  nzType="text"
                  nzSize="small"
                  (click)="removeFile(index)"
                  class="ml-2"
                >
                  <i nz-icon nzType="close" nzTheme="outline"></i>
                </button>
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- Buttons -->
    <div class="flex justify-end gap-4 mt-6">
      <button type="button" nz-button (click)="onCancel()">Cancel</button>
      <button type="button" nz-button nzType="primary" [disabled]="form.invalid || isSubmitting()" (click)="next()">
        Next
      </button>
    </div>
  </form>
}

@if (currentPage() === 1) {
  <!-- Page 2: Confirmation -->
  <div class="p-6">
    <div class="space-y-4 mb-6">
      <p>
        Handshake acts as a <strong>neutral third party</strong>. Once a dispute is raised, the associated project and
        related funds will be <span class="text-red-600 font-semibold">immediately locked</span> until a mutual
        resolution is reached or a settlement agreement is uploaded.
      </p>

      <p>
        The <strong>Handshake Dispute Resolution Team</strong> will always be contactable
        <strong>directly via Whatsapp or Email</strong> to guide both parties through the dispute resolution process. We
        can help explain available options, outline typical timelines and considerations, and provide facilitation
        support where possible — <strong>without acting as a decision maker or taking sides</strong>.
      </p>

      <div class="mt-6">
        <h3 class="font-semibold mb-2">Before You Proceed</h3>
        <ul class="list-disc pl-6 space-y-1">
          <li>
            We recommend starting with an <strong>informal discussion</strong> to resolve the issue directly whenever
            possible.
          </li>
          <li>
            <strong>Formal legal processes</strong> may involve additional costs and longer timelines.
          </li>
        </ul>
      </div>

      <p class="mt-4">If you're certain you'd like to proceed, please confirm by pressing submit below.</p>
    </div>

    <!-- Buttons -->
    <div class="flex justify-end gap-4 mt-6">
      <button type="button" nz-button (click)="back()">Back</button>
      <button
        type="button"
        nz-button
        nzType="primary"
        [nzLoading]="isSubmitting()"
        [disabled]="isSubmitting()"
        (click)="onSubmit()"
        class="bg-purple-600 hover:bg-purple-700"
      >
        Submit
      </button>
    </div>
  </div>
}
